import _ from 'lodash';
import { Record as record, List } from 'immutable';
import moment from 'moment';

import Leg from './Leg';
import Bulletin from './Bulletin';
import StationStop from './StationStop';
import CallingPoints from './CallingPoints';
import CancellationReason from './CancellationReason';
import { resolve, resolveAll } from '../helpers/LinkMapper';
import { JourneyStatus, AdjustedTimeConfidence } from '../constants/AppConstants';

const defaults = {
    id: null,
    origin: new StationStop(),
    destination: new StationStop(),
    changes: 0,
    legs: new List(),
    bulletins: new List(),
    callingPoints: new CallingPoints(),
    isOvertaken: false,
    isCancelled: false,
    status: JourneyStatus.SUCCESSFUL,
    cancellationReason: null
};

class Journey extends record(defaults) {
    static fromJSON (json) {
        const legs = resolveAll(json.legs).as(Leg);
        const changes = legs.count() - 1 || 0;

        return new Journey({
            origin: resolve(json.origin).as(StationStop),
            destination: resolve(json.destination).as(StationStop),
            changes: resolve(changes).with(Number),
            legs: legs,
            bulletins: resolveAll(json.bulletins).as(Bulletin),
            callingPoints: json.callingPoints,
            isOvertaken: resolve(json.isOvertaken).with(Boolean),
            isCancelled: resolve(json.isCancelled).with(Boolean),
            status: resolve(json.status).with(String),
            cancellationReason: resolve(json.cancellationReason).as(CancellationReason)
        });
    }

    getOriginStation () {
        return this.origin.station;
    }

    getOriginStationCrs () {
        return this.getOriginStation().crs;
    }

    getDestinationStation () {
        return this.destination.station;
    }

    getDestinationStationCrs () {
        return this.destination.station.crs;
    }

    isAdjustedDepartureTime () {
        return _.result(this.origin, 'isAdjustedTime');
    }

    isAdjustedArrivalTime () {
        return _.result(this.destination, 'isAdjustedTime');
    }

    isArrivalDelayed () {
        return _.result(this.destination, 'isDelayed');
    }

    isIndeterminateDelay () {
        return this.destination.time.confidence === AdjustedTimeConfidence.UNCERTAIN
            && this.isArrivalDelayed();
    }

    getDepartureDateTime (adjusted = true) {
        return _.result(this.origin.getTime(adjusted), 'clone');
    }

    getArrivalDateTime (adjusted = true) {
        return _.result(this.destination.getTime(adjusted), 'clone');
    }

    getTripDuration () {
        return moment.duration(this.getArrivalDateTime().diff(this.getDepartureDateTime()));
    }

    operators () {
        return this.legs
            .map((leg) => leg.getIn(['serviceDetails', 'toc']))
            .toSet()
            .filter((toc) => toc)
            .toList();
    }

    isDisrupted () {
        return this.status !== JourneyStatus.SUCCESSFUL;
    }

    getCancellationMessage () {
        return _.result(this.cancellationReason, 'message');
    }

    hasBulletins () {
        return !this.bulletins.isEmpty();
    }

    hasP1bulletin () {
        return this.hasBulletins() && this.bulletins.some(bulletin => bulletin.isP1());
    }

    getCountedPlaceLegs () {
        return this.legs.filter(leg =>
            leg.getIn(['serviceDetails', 'isCountedPlace']),
        );
    }

    getDepartureDelay () {
        return this.getDepartureDateTime().calendarDiff(this.getDepartureDateTime(false));
    }
}

export default Journey;



// WEBPACK FOOTER //
// ./src/records/Journey.js